/**
 * `WorkflowRevertState.ts` — web port of the snapshot-and-restore half of
 * Unity's `Models/Data/ActionData.cs`: `PreviewRestoreState` (the captured
 * bag, `MergeFrom`, `RestoreAsync`) and `CaptureToolPreviewRestoreState`
 * (which domains a given tool can touch).
 *
 * Revert is snapshot-and-restore, NOT an inverse-command stack. The pre-tool
 * state of everything a step can touch is captured BEFORE the step runs,
 * captures merge FIRST-WINS across the script (a part touched twice keeps the
 * value it had before the script started), and one pass at the end puts it
 * back in memory. That shape is what makes tools with no inverse (`reset_scene`)
 * revertible, and it is why script revert stays independent of the editor's
 * `IUndoRedo` stack — a guide script must not rewrite the learner's own undo
 * history to clean up after itself.
 *
 * The same capture also records SPARK's own changes during a run (a learner
 * asking it to isolate something), without view state, so the run can undo
 * them at the step change — see `WorkflowScriptRunner.revertLiveChanges` and
 * {@link WorkflowRevertState.takeOverlapping}.
 *
 * Captured domains and their web owners: entity snapshot rows (transform,
 * visibility, display name, material assignment, constant spin), the material
 * ROWS those assignments point at (`apply_material_on_parts` overwrites the
 * row in place on web, so restoring the assignment alone would revert
 * nothing), media-tile visibility, explosion factor, scene isolation, camera
 * pose, and selection.
 *
 * Animation settings (take, speed, direction, playback type and autoplay) are
 * captured per ENTITY for `Toggle_Model_Animation`, which is the one tool that
 * writes them — Unity captures the same domain (`ScriptData.cs:314-321`).
 *
 * Not captured, because the web has no reader for the state or no tool that
 * writes it: action-button visibility,
 * selection-set pose, the cutting plane, and part descriptions
 * (`update_part_context` writes entity settings, which the model exposes no
 * getter for). Structural edits — creating, deleting, renaming or reordering
 * scenes and models — are deliberately NOT revertible, matching Unity: its
 * capture switch has no branch for them either.
 */

import { AgentToolNames } from "@ai/registry/agentToolNames";
import {
  isPartPathArgName,
  isSelectionSetArgName,
  isTextBoxArgName,
} from "@ai/registry/scriptCommandCatalog";
import {
  buildPathIndex,
  resolveManyByPath,
  type PathEntry,
} from "@ai/context/hierarchyPaths";
import { ENTITY_ANIMATION_PLAYBACK_TYPE } from "@models/data/entityData";
import type {
  EntityAnimationSettings,
  EntitySettingsPatch,
  EntitySnapshot,
  EntityTransform,
  Vec3,
} from "@models/data/entityData";
import type { MaterialDto } from "@models/data/materialDto";
import type { IExplosionModifierModel } from "@models/modifiers/IExplosionModifierModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import { beginWorkflowPerformanceOperation } from "@models/project/SceneNavigationDiagnosticLogger";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import { parsePersistentSpinJson } from "@models/data/persistentSpinData";
import { parsePersistentHighlightJson } from "@models/data/persistentHighlightData";

/**
 * Camera seam. `src/models/**` must not import `src/runtime/**`, and the orbit
 * camera is a runtime fact, so the owner injects this the same way
 * `WorkflowRunModel` takes its headset probe.
 */
export interface WorkflowRevertCamera {
  getPose(): { readonly position: Vec3; readonly target: Vec3 };
  setPose(position: Vec3, target: Vec3): void;
}

export interface WorkflowRevertDependencies {
  readonly project: IProjectModel;
  readonly selection: ISelectionModel;
  readonly explosion: IExplosionModifierModel;
  /** `null` when no viewport is mounted (headless, projects grid). */
  readonly camera: () => WorkflowRevertCamera | null;
}

type ToolArgs = Readonly<Record<string, unknown>>;

/**
 * Whether camera pose and selection — the two VIEW domains — ride a capture or
 * a restore. Both default to true, which is the script behaviour Unity has.
 *
 * Off for Spark's own changes during a run, and for a restore a step move
 * triggers: a guided run's camera belongs to the step being arrived at (its
 * `step_view`, or its scene's saved view), so a revert that also put the
 * camera back would land after that framing and pull the learner away from it.
 */
export interface WorkflowRevertViewOptions {
  readonly includeViewState?: boolean;
}

interface SnapshotRevert {
  readonly transform: EntityTransform;
  readonly visibility: boolean;
  readonly forcedVisibility: boolean;
  readonly displayName: string;
  readonly materialIds: ReadonlyArray<string>;
  readonly persistentSpinJson: string | null;
  readonly persistentHighlightJson: string | null;
}

/**
 * Argument keys that name revert targets come from the registry's own naming
 * convention (`partPathsToHide`, `selectionSetNameToSelect`,
 * `textBoxNamesToUnhide`, `modelPathsToExplode`), which replaces Unity's six
 * hand-maintained key arrays — a tool added later is covered with no edit.
 *
 * The predicates live in `scriptCommandCatalog` rather than here because the
 * AUTHORING UI asks the same question of the same arguments when it decides
 * which control an argument needs. Two copies of the convention would let the
 * snapshot and the editor disagree about which arguments name parts.
 */

/** One scene's captured pre-script state, and the pass that puts it back. */
export class WorkflowRevertState {
  private readonly m_snapshots = new Map<string, SnapshotRevert>();
  private readonly m_materials = new Map<string, MaterialDto>();
  private readonly m_mediaTileVisibility = new Map<string, boolean>();
  private readonly m_explosionFactors = new Map<string, number>();
  /**
   * Keyed by ENTITY id, not snapshot id — animation settings live on the
   * entity (`updateEntitySettingsAsync(sceneId, entity.id, …)`), and every
   * part of a model shares one set.
   */
  private readonly m_animationSettings = new Map<string, EntityAnimationSettings>();
  private m_isolatedSnapshotIds: ReadonlyArray<string> | null = null;
  private m_camera: { readonly position: Vec3; readonly target: Vec3 } | null = null;
  private m_selectedIds: ReadonlyArray<string> | null = null;

  constructor(readonly sceneId: string) {}

  get hasRestorableState(): boolean {
    return (
      this.m_snapshots.size > 0 ||
      this.m_materials.size > 0 ||
      this.m_mediaTileVisibility.size > 0 ||
      this.m_explosionFactors.size > 0 ||
      this.m_animationSettings.size > 0 ||
      this.m_isolatedSnapshotIds !== null ||
      this.m_camera !== null ||
      this.m_selectedIds !== null
    );
  }

  /** Unity `PreviewRestoreState.MergeFrom`: every field is first-capture-wins. */
  mergeFrom(other: WorkflowRevertState | null): void {
    if (!other || other.sceneId !== this.sceneId) return;

    for (const [id, revert] of other.m_snapshots) {
      if (!this.m_snapshots.has(id)) this.m_snapshots.set(id, revert);
    }
    for (const [id, dto] of other.m_materials) {
      if (!this.m_materials.has(id)) this.m_materials.set(id, dto);
    }
    for (const [id, visibility] of other.m_mediaTileVisibility) {
      if (!this.m_mediaTileVisibility.has(id)) this.m_mediaTileVisibility.set(id, visibility);
    }
    for (const [id, factor] of other.m_explosionFactors) {
      if (!this.m_explosionFactors.has(id)) this.m_explosionFactors.set(id, factor);
    }
    for (const [id, settings] of other.m_animationSettings) {
      if (!this.m_animationSettings.has(id)) this.m_animationSettings.set(id, settings);
    }
    if (this.m_isolatedSnapshotIds === null) {
      this.m_isolatedSnapshotIds = other.m_isolatedSnapshotIds;
    }
    if (this.m_camera === null) this.m_camera = other.m_camera;
    if (this.m_selectedIds === null) this.m_selectedIds = other.m_selectedIds;
  }

  captureCamera(deps: WorkflowRevertDependencies): void {
    const pose = deps.camera()?.getPose();
    if (pose) this.m_camera = { position: pose.position, target: pose.target };
  }

  captureSelection(deps: WorkflowRevertDependencies): void {
    this.m_selectedIds = [...deps.selection.selectedIds];
  }

  captureIsolation(deps: WorkflowRevertDependencies): void {
    const scene = deps.project.getScene(this.sceneId);
    this.m_isolatedSnapshotIds = scene ? [...scene.isolatedSnapshotIds] : [];
  }

  captureSnapshots(
    targets: ReadonlyArray<PathEntry>,
    deps: WorkflowRevertDependencies,
  ): void {
    for (const target of targets) {
      const snapshot = target.snapshot;
      if (this.m_snapshots.has(snapshot.id)) continue;
      this.m_snapshots.set(snapshot.id, {
        transform: snapshot.transform,
        visibility: snapshot.visibility,
        forcedVisibility: snapshot.forcedVisibility,
        displayName: snapshot.displayName,
        materialIds: [...snapshot.materialIds],
        persistentSpinJson: snapshot.persistentSpinJson,
        persistentHighlightJson: snapshot.persistentHighlightJson,
      });
      this.captureMaterialRows(snapshot, deps);
    }
  }

  captureMediaTileVisibility(tileIds: ReadonlyArray<string>, deps: WorkflowRevertDependencies): void {
    for (const tileId of tileIds) {
      if (this.m_mediaTileVisibility.has(tileId)) continue;
      const tile = deps.project.getMediaTile(this.sceneId, tileId);
      if (tile) this.m_mediaTileVisibility.set(tileId, tile.visibility);
    }
  }

  /**
   * The five animation controls, per owning ENTITY.
   *
   * `PathEntry.entity` is the owning root entity at any depth, so a target
   * deep inside a model captures the model's settings with no `rootPathOf`
   * promotion — which matches the executor, which promotes to the root before
   * writing.
   */
  captureAnimationSettings(
    targets: ReadonlyArray<PathEntry>,
    _deps: WorkflowRevertDependencies,
  ): void {
    for (const target of targets) {
      const entityId = target.entity.id;
      if (this.m_animationSettings.has(entityId)) continue;
      this.m_animationSettings.set(entityId, target.entity.animationSettings);
    }
  }

  /** Every root's factor, matching Unity's scene-wide `AddExplosionPreviewSnapshots`. */
  captureExplosionFactors(deps: WorkflowRevertDependencies): void {
    for (const entry of buildPathIndex(deps.project.getEntities(this.sceneId))) {
      if (!entry.isRoot || this.m_explosionFactors.has(entry.snapshot.id)) continue;
      this.m_explosionFactors.set(
        entry.snapshot.id,
        deps.explosion.getExplosionFactor(this.sceneId, entry.snapshot.id),
      );
    }
  }

  /**
   * Moves every item `other` also holds out of this bag and into a new one:
   * the same snapshot rows, material rows, media tiles, explosion roots and
   * animation entities, and the scene's isolation. Camera and selection are
   * not items and stay where they are.
   *
   * For the precedence between Spark's own changes during a run and the
   * step's script (`WorkflowScriptRunner`): when the script writes something
   * Spark had changed, Spark's claim on it ends there.
   *
   * Row-granular, like capture itself: a script that hides a part Spark
   * highlighted takes the whole row, highlight included.
   */
  takeOverlapping(other: WorkflowRevertState): WorkflowRevertState {
    const taken = new WorkflowRevertState(this.sceneId);
    if (other.sceneId !== this.sceneId) return taken;
    moveSharedKeys(this.m_snapshots, other.m_snapshots, taken.m_snapshots);
    moveSharedKeys(this.m_materials, other.m_materials, taken.m_materials);
    moveSharedKeys(this.m_mediaTileVisibility, other.m_mediaTileVisibility, taken.m_mediaTileVisibility);
    moveSharedKeys(this.m_explosionFactors, other.m_explosionFactors, taken.m_explosionFactors);
    moveSharedKeys(this.m_animationSettings, other.m_animationSettings, taken.m_animationSettings);
    if (other.m_isolatedSnapshotIds !== null && this.m_isolatedSnapshotIds !== null) {
      taken.m_isolatedSnapshotIds = this.m_isolatedSnapshotIds;
      this.m_isolatedSnapshotIds = null;
    }
    return taken;
  }

  /**
   * Puts the captured state back. Every update is skipped when the live value
   * already matches. During Workflow playback these ProjectModel calls remain
   * inside the transient mutation scope, so restore updates local models only:
   * no database write, project broadcast, or thumbnail upload occurs.
   */
  async restoreAsync(
    deps: WorkflowRevertDependencies,
    options: WorkflowRevertViewOptions = {},
  ): Promise<void> {
    const endPerformanceOperation = beginWorkflowPerformanceOperation("workflow revert", {
      sceneId: this.sceneId,
      capturedSnapshotCount: this.m_snapshots.size,
      capturedMaterialCount: this.m_materials.size,
      capturedMediaTileCount: this.m_mediaTileVisibility.size,
      capturedAnimationCount: this.m_animationSettings.size,
    });
    const project = deps.project;

    if (this.m_isolatedSnapshotIds !== null) {
      const current = project.getScene(this.sceneId)?.isolatedSnapshotIds ?? [];
      if (!isSameIdList(current, this.m_isolatedSnapshotIds)) {
        await this.guard(() =>
          project.updateSceneIsolationMode(this.sceneId, this.m_isolatedSnapshotIds ?? []));
      }
    }

    // A script's material edit can have FORKED a library-owned row into a
    // project-private copy (`ProjectModelMaterialFork`), which re-points the
    // snapshot. Resolve that first: restoring the captured id onto the
    // snapshot would hand it back to the library and undo the isolation, and
    // restoring only the library ROW would leave the part wearing the
    // script's material. Instead, put the captured VALUES onto the copy the
    // snapshot now names, and leave the assignment alone.
    const forkedRows = this.collectForkedMaterialRestores(project);

    // Material rows before assignments: the assignment restore below points at
    // these rows, and `apply_material_on_parts` edits the row in place.
    for (const [materialId, dto] of this.m_materials) {
      const current = project.getMaterialData(materialId);
      if (!current || isSameJson(current, dto)) continue;
      await this.guard(() => project.updateMaterialDataAsync(dto));
    }
    for (const [copyId, dto] of forkedRows) {
      const current = project.getMaterialData(copyId);
      if (current && isSameJson(current, dto)) continue;
      await this.guard(() => project.updateMaterialDataAsync(dto));
    }

    const snapshotUpdates: Array<{
      snapshotId: string;
      patch: NonNullable<ReturnType<typeof buildSnapshotRestorePatch>>;
    }> = [];
    for (const [snapshotId, revert] of this.m_snapshots) {
      const current = project.getEntitySnapshotById(this.sceneId, snapshotId);
      if (!current) continue;
      const patch = buildSnapshotRestorePatch(current, revert, forkedRows);
      if (!patch) continue;
      snapshotUpdates.push({ snapshotId, patch });
    }
    if (snapshotUpdates.length > 0) {
      await this.guard(() => project.updateEntitySnapshotsAsync(this.sceneId, snapshotUpdates));
    }

    for (const [tileId, visibility] of this.m_mediaTileVisibility) {
      const tile = project.getMediaTile(this.sceneId, tileId);
      if (!tile || tile.visibility === visibility) continue;
      await this.guard(() =>
        project.updateMediaTileAsync(this.sceneId, tileId, { visibility }, ["Visibility"]));
    }

    for (const [rootSnapshotId, factor] of this.m_explosionFactors) {
      if (deps.explosion.getExplosionFactor(this.sceneId, rootSnapshotId) === factor) continue;
      await this.guard(() =>
        deps.explosion.commitExplosionFactorAsync(this.sceneId, rootSnapshotId, factor));
    }

    // MODEL state, so it restores whether or not the scene is still on screen
    // — above the active-scene gate below, which exists for the two VIEW ones.
    for (const [entityId, settings] of this.m_animationSettings) {
      const current = project.getEntityById(this.sceneId, entityId)?.animationSettings;
      if (!current || sameAnimationSettings(current, settings)) continue;
      await this.guard(() =>
        project.updateEntitySettingsAsync(this.sceneId, entityId, {
          animation: animationRestorePatch(settings),
        }));
    }

    // Camera and selection are VIEW state — restoring them into a scene that
    // is no longer on screen would yank the camera the learner is currently
    // using. Unity gates both on the same `isActiveScene` test. A caller can
    // also opt out: a restore a step move triggers must leave the camera to
    // the step being arrived at (`WorkflowRevertViewOptions`).
    if (project.activeSceneId !== this.sceneId || options.includeViewState === false) {
      endPerformanceOperation();
      return;
    }
    if (this.m_camera) {
      deps.camera()?.setPose(this.m_camera.position, this.m_camera.target);
    }
    if (this.m_selectedIds) deps.selection.set(this.m_selectedIds);
    endPerformanceOperation();
  }

  /**
   * Map every captured material id that has since been forked to the copy the
   * snapshot now names, carrying the captured row's values onto it.
   *
   * Position-wise, which is exact: `remapMaterialIds` preserves order and
   * length, so slot i of the captured list and slot i of the live list are the
   * same material slot. A difference at slot i where the captured id is
   * library-owned is a fork and nothing else — a script REASSIGNING a material
   * changes the id to one the library does not own.
   */
  private collectForkedMaterialRestores(
    project: WorkflowRevertDependencies["project"],
  ): Map<string, MaterialDto> {
    const out = new Map<string, MaterialDto>();
    for (const [snapshotId, revert] of this.m_snapshots) {
      const live = project.getEntitySnapshotById(this.sceneId, snapshotId);
      if (!live || live.materialIds.length !== revert.materialIds.length) continue;
      for (let i = 0; i < revert.materialIds.length; i++) {
        const capturedId = revert.materialIds[i];
        const liveId = live.materialIds[i];
        if (!capturedId || !liveId || capturedId === liveId) continue;
        if (!project.isLibraryOwnedMaterial(capturedId)) continue;
        const dto = this.m_materials.get(capturedId);
        if (dto && !out.has(liveId)) out.set(liveId, { ...dto, ID: liveId });
      }
    }
    return out;
  }

  private captureMaterialRows(snapshot: EntitySnapshot, deps: WorkflowRevertDependencies): void {
    for (const materialId of snapshot.materialIds) {
      if (this.m_materials.has(materialId)) continue;
      const dto = deps.project.getMaterialData(materialId);
      // Cloned: the row is edited IN PLACE by `applyLibraryMaterialToSnapshot`,
      // so holding the live object would capture the post-tool colour.
      if (dto) this.m_materials.set(materialId, structuredClone(dto));
    }
  }

  /**
   * One failed write must not abandon the rest of the revert — a scene left
   * half-restored is worse than one that reports a warning and finishes.
   */
  private async guard(write: () => Promise<unknown>): Promise<void> {
    try {
      await write();
    } catch (e) {
      console.warn("[WorkflowRevertState] Failed to restore part of the script state:", e);
    }
  }
}

/**
 * Per-scene revert bags. Doubles as the accumulator for one script run and as
 * the queue behind `revert: on_scene_change`.
 *
 * Keyed by scene because a script may navigate mid-run: Unity handles that by
 * driving the camera back to the initial scene before restoring, which the web
 * does not need — the model is the source of truth, so a write lands whether
 * or not that scene is currently built.
 */
export class WorkflowRevertLedger {
  private readonly m_byScene = new Map<string, WorkflowRevertState>();

  get hasRestorableState(): boolean {
    for (const state of this.m_byScene.values()) {
      if (state.hasRestorableState) return true;
    }
    return false;
  }

  get sceneIds(): ReadonlyArray<string> {
    return [...this.m_byScene.keys()];
  }

  /** Captures the pre-tool state for one step and merges it in (first wins). */
  capture(
    toolName: string,
    args: ToolArgs,
    deps: WorkflowRevertDependencies,
    options: WorkflowRevertViewOptions = {},
  ): void {
    const captured = captureToolRevertState(toolName, args, deps, options);
    if (!captured?.hasRestorableState) return;
    this.merge(captured);
  }

  merge(state: WorkflowRevertState): void {
    const existing = this.m_byScene.get(state.sceneId);
    if (existing) {
      existing.mergeFrom(state);
      return;
    }
    this.m_byScene.set(state.sceneId, state);
  }

  mergeLedger(other: WorkflowRevertLedger): void {
    for (const state of other.m_byScene.values()) this.merge(state);
  }

  /** Removes and returns one scene's bag. */
  take(sceneId: string): WorkflowRevertState | null {
    const state = this.m_byScene.get(sceneId) ?? null;
    if (state) this.m_byScene.delete(sceneId);
    return state;
  }

  takeAll(): ReadonlyArray<WorkflowRevertState> {
    const states = [...this.m_byScene.values()];
    this.m_byScene.clear();
    return states;
  }

  /**
   * Moves every item `other` also holds out of this ledger into a new one, per
   * scene (see {@link WorkflowRevertState.takeOverlapping}). A bag left with
   * nothing to restore is dropped, so {@link hasRestorableState} stays honest.
   */
  takeOverlapping(other: WorkflowRevertLedger | WorkflowRevertState): WorkflowRevertLedger {
    const taken = new WorkflowRevertLedger();
    const others = other instanceof WorkflowRevertLedger ? [...other.m_byScene.values()] : [other];
    for (const otherState of others) {
      const mine = this.m_byScene.get(otherState.sceneId);
      if (!mine) continue;
      const overlap = mine.takeOverlapping(otherState);
      if (overlap.hasRestorableState) taken.merge(overlap);
      if (!mine.hasRestorableState) this.m_byScene.delete(otherState.sceneId);
    }
    return taken;
  }

  /** Drains every bag and restores it. Scene order is capture order. */
  async restoreAllAsync(
    deps: WorkflowRevertDependencies,
    options: WorkflowRevertViewOptions = {},
  ): Promise<void> {
    for (const state of this.takeAll()) {
      await state.restoreAsync(deps, options);
    }
  }
}

/**
 * Port of Unity `CaptureToolPreviewRestoreState`: the pre-tool state of every
 * domain this specific tool can write. `null` when there is no active scene.
 */
export function captureToolRevertState(
  toolName: string,
  args: ToolArgs,
  deps: WorkflowRevertDependencies,
  options: WorkflowRevertViewOptions = {},
): WorkflowRevertState | null {
  const sceneId = deps.project.activeSceneId;
  if (!sceneId) return null;

  const tool = toolName.trim().toLowerCase();
  const state = new WorkflowRevertState(sceneId);
  // Unity's `CaptureBasePreviewRestoreState` takes the camera for every step;
  // selection rides along, so a script that selects a part to talk about it
  // hands the learner's own selection back at the end.
  if (options.includeViewState !== false) {
    state.captureCamera(deps);
    state.captureSelection(deps);
  }

  if (
    isTool(tool, AgentToolNames.Isolate) ||
    isTool(tool, AgentToolNames.ClearIsolation) ||
    (isTool(tool, AgentToolNames.FocusOnPart) && args["useIsolate"] === true)
  ) {
    state.captureIsolation(deps);
  }

  state.captureSnapshots(resolveToolTargets(tool, args, deps, sceneId), deps);
  state.captureMediaTileVisibility(resolveMediaTileTargets(args, deps, sceneId), deps);

  if (
    isTool(tool, AgentToolNames.Explode) ||
    isTool(tool, AgentToolNames.UnexplodeModel) ||
    isTool(tool, AgentToolNames.ResetScene)
  ) {
    state.captureExplosionFactors(deps);
  }

  if (isTool(tool, AgentToolNames.ToggleModelAnimation)) {
    // ⚠ `resolveToolTargets` stops at the selection; the EXECUTOR has one more
    // fallback — the sole root in the scene when there is no `partPath` and
    // nothing selected. Mirror it, or an authored `Toggle_Model_Animation`
    // step with only `enabled` writes to that root and captures NOTHING, and
    // the script permanently changes the author's project.
    let targets = resolveToolTargets(tool, args, deps, sceneId);
    if (targets.length === 0) {
      const roots = buildPathIndex(deps.project.getEntities(sceneId))
        .filter((entry) => entry.isRoot);
      if (roots.length === 1) targets = roots;
    }
    state.captureAnimationSettings(targets, deps);
  }

  return state;
}

/** `EntityAnimationSettings` → the patch shape that reproduces it exactly. */
function animationRestorePatch(
  settings: EntityAnimationSettings,
): NonNullable<EntitySettingsPatch["animation"]> {
  return {
    autoplay: settings.autoplay,
    playback: settings.playbackType === ENTITY_ANIMATION_PLAYBACK_TYPE.Once
      ? "once"
      : settings.playbackType === ENTITY_ANIMATION_PLAYBACK_TYPE.PingPong
        ? "pingPong"
        : "loop",
    // Direction is the SIGN of speed, not a field; the patch splits them and
    // `applyEntityAnimationSettingsPatch` recombines them.
    speedPercent: Math.abs(settings.speed) * 100,
    direction: settings.speed < 0 ? "backward" : "forward",
    // `-1` ("the importer's default clip") round-trips: the patch guards on a
    // non-EMPTY string and then `parseInt`s it, and `"-1"` satisfies both.
    take: String(settings.clipIndex),
  };
}

function sameAnimationSettings(
  a: EntityAnimationSettings,
  b: EntityAnimationSettings,
): boolean {
  return a.autoplay === b.autoplay
    && a.playbackType === b.playbackType
    && a.speed === b.speed
    && a.clipIndex === b.clipIndex;
}

/** Port of Unity `ResolveToolPreviewTargets`. */
function resolveToolTargets(
  tool: string,
  args: ToolArgs,
  deps: WorkflowRevertDependencies,
  sceneId: string,
): ReadonlyArray<PathEntry> {
  const entities = deps.project.getEntities(sceneId);
  const index = buildPathIndex(entities);

  // `reset_scene` rewrites every moved part in the scene.
  if (isTool(tool, AgentToolNames.ResetScene)) return index;
  // The hide/unhide all-flags write every root.
  if (args["hideAllParts"] === true || args["unhideAllParts"] === true) {
    return index.filter((entry) => entry.isRoot);
  }

  const paths: string[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (isPartPathArgName(key)) paths.push(...toStringList(value));
  }
  const targets = resolveManyByPath(entities, paths);

  for (const [key, value] of Object.entries(args)) {
    if (!isSelectionSetArgName(key)) continue;
    for (const setName of toStringList(value)) {
      const set = deps.project
        .getSelectionSets(sceneId)
        .find((candidate) => candidate.Name.toLowerCase() === setName.toLowerCase());
      for (const snapshotId of set?.EntitySnapshotIDs ?? []) {
        const entry = index.find((candidate) => candidate.snapshot.id === snapshotId);
        if (entry) targets.push(entry);
      }
    }
  }
  if (targets.length > 0) return targets;

  // `clear_constant_spin` with no targets clears every spinning part.
  if (isTool(tool, AgentToolNames.ClearConstantSpin)) {
    return index.filter(
      (entry) => parsePersistentSpinJson(entry.snapshot.persistentSpinJson) !== null,
    );
  }
  // `clear_highlight` with no targets clears every highlighted part.
  if (isTool(tool, AgentToolNames.ClearHighlight)) {
    return index.filter(
      (entry) => parsePersistentHighlightJson(entry.snapshot.persistentHighlightJson) !== null,
    );
  }
  // Every other executor falls back to the current selection.
  return index.filter((entry) => deps.selection.isSelected(entry.snapshot.id));
}

/** Media tiles ("text boxes") ride the hide/unhide tools by NAME. */
function resolveMediaTileTargets(
  args: ToolArgs,
  deps: WorkflowRevertDependencies,
  sceneId: string,
): ReadonlyArray<string> {
  const names: string[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (isTextBoxArgName(key)) names.push(...toStringList(value));
  }
  if (names.length === 0) return [];

  return deps.project
    .getMediaTiles(sceneId)
    .filter((tile) => names.some((name) => name.toLowerCase() === tile.name.toLowerCase()))
    .map((tile) => tile.id);
}

/** The patch that would put `current` back to `revert`, or null when equal. */
/**
 * True when every slot where the live and captured material lists disagree is
 * one whose live id is a fork we are restoring by value. Such a difference is
 * intentional and must not be "restored" away.
 */
function isForkOnlyDifference(
  currentIds: ReadonlyArray<string>,
  revertIds: ReadonlyArray<string>,
  forkedRows: ReadonlyMap<string, MaterialDto>,
): boolean {
  if (currentIds.length !== revertIds.length) return false;
  let sawFork = false;
  for (let i = 0; i < currentIds.length; i++) {
    if (currentIds[i] === revertIds[i]) continue;
    if (!forkedRows.has(currentIds[i])) return false;
    sawFork = true;
  }
  return sawFork;
}

function buildSnapshotRestorePatch(
  current: EntitySnapshot,
  revert: SnapshotRevert,
  forkedRows: ReadonlyMap<string, MaterialDto>,
): {
  transform?: EntityTransform;
  visibility?: boolean;
  forcedVisibility?: boolean;
  displayName?: string;
  materialIds?: ReadonlyArray<string>;
  persistentSpinJson?: string | null;
  persistentHighlightJson?: string | null;
} | null {
  const patch: {
    transform?: EntityTransform;
    visibility?: boolean;
    forcedVisibility?: boolean;
    displayName?: string;
    materialIds?: ReadonlyArray<string>;
    persistentSpinJson?: string | null;
    persistentHighlightJson?: string | null;
  } = {};

  if (!isSameTransform(current.transform, revert.transform)) patch.transform = revert.transform;
  if (current.visibility !== revert.visibility) patch.visibility = revert.visibility;
  if (current.forcedVisibility !== revert.forcedVisibility) {
    patch.forcedVisibility = revert.forcedVisibility;
  }
  if (current.displayName !== revert.displayName) patch.displayName = revert.displayName;
  // Skip the assignment when the only difference is a fork we are restoring
  // by value — re-pointing would put the snapshot back on the library's row.
  if (
    !isSameIdList(current.materialIds, revert.materialIds)
    && !isForkOnlyDifference(current.materialIds, revert.materialIds, forkedRows)
  ) {
    patch.materialIds = revert.materialIds;
  }
  if (current.persistentSpinJson !== revert.persistentSpinJson) {
    patch.persistentSpinJson = revert.persistentSpinJson;
  }
  if (current.persistentHighlightJson !== revert.persistentHighlightJson) {
    patch.persistentHighlightJson = revert.persistentHighlightJson;
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

/** Moves each entry of `from` whose key `probe` also has into `to`. */
function moveSharedKeys<V>(
  from: Map<string, V>,
  probe: ReadonlyMap<string, unknown>,
  to: Map<string, V>,
): void {
  for (const key of probe.keys()) {
    if (!from.has(key)) continue;
    to.set(key, from.get(key) as V);
    from.delete(key);
  }
}

function isTool(normalizedToolName: string, tool: string): boolean {
  return normalizedToolName === tool.toLowerCase();
}

function toStringList(value: unknown): string[] {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? [trimmed] : [];
  }
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is string => typeof entry === "string")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function isSameTransform(a: EntityTransform, b: EntityTransform): boolean {
  return (
    isSameVec3(a.position, b.position) &&
    isSameVec3(a.rotation, b.rotation) &&
    isSameVec3(a.scale, b.scale)
  );
}

function isSameVec3(a: Vec3, b: Vec3): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

function isSameIdList(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function isSameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
